import { nip19 } from 'nostr-tools';
import type { NostrMetadata } from '@nostrify/nostrify';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EngagementAction = 'like' | 'repost' | 'comment';

export interface ParsedNoteInput {
  /** The original token the user typed/pasted */
  raw: string;
  /** Hex event id (present when valid or duplicate) */
  hex?: string;
  status: 'valid' | 'duplicate' | 'invalid';
  /** Human readable reason when invalid */
  error?: string;
}

export interface EngagementSettings {
  likes: boolean;
  reposts: boolean;
  comments: boolean;
}

export interface Participant {
  pubkey: string;
  npub: string;
  /** Which actions qualified them (deduplicated) */
  actions: EngagementAction[];
  /** Which of the raffle notes they interacted with (hex ids) */
  noteIds: string[];
  /** Relays their engagement events were seen on */
  relays: string[];
}

export interface EngagementTotals {
  likes: number;
  reposts: number;
  comments: number;
  rawEvents: number;
}

export interface EngagementResult {
  participants: Participant[];
  totals: EngagementTotals;
  /** Relays that answered successfully */
  okRelays: string[];
  /** Relays that failed or timed out */
  failedRelays: string[];
  /** kind-0 metadata per pubkey */
  profiles: Record<string, NostrMetadata>;
  /** Event ids of all engagement events found (used to exclude them from activity checks) */
  engagementEventIds: string[];
  notesScanned: number;
}

/** Signals gathered from relays about a participant's wider Nostr activity */
export interface ActivitySignals {
  /** Has a contact list with at least one follow */
  hasFollows: boolean;
  followCount: number;
  /** At least one other account follows them */
  hasFollowers: boolean;
  /** Number of events found beyond the raffle interactions (sampled) */
  activityCount: number;
  /** Oldest event timestamp we saw for this account (unix seconds) */
  oldestEventAt?: number;
  /** Followed by one of the trusted seed accounts */
  followedBySeed: boolean;
}

export interface TrustResult {
  pubkey: string;
  score: number;
  /** Plain-language reasons that added points */
  positives: string[];
  /** Plain-language reasons that removed points or are missing */
  negatives: string[];
}

export interface Ticket {
  number: number;
  participant: Participant;
  trust: TrustResult;
}

export interface FilteredOutEntry {
  participant: Participant;
  trust: TrustResult;
  /** Plain-language reasons they were filtered out */
  reasons: string[];
}

export interface DrawOutcome {
  drawnNumber: number;
  winner: Ticket;
  winnerDistance: number;
  /** All tickets that were equally close (tie) — winner is the lowest ticket among these */
  winnerTies: Ticket[];
  runnerUp?: Ticket;
  runnerUpDistance?: number;
  runnerUpTies: Ticket[];
}

// ---------------------------------------------------------------------------
// Note ID parsing
// ---------------------------------------------------------------------------

const HEX64 = /^[0-9a-fA-F]{64}$/;

/**
 * Parse a free-form string of note IDs separated by newlines, spaces,
 * commas or semicolons. Supports note1…, nevent1… and raw hex ids.
 * Duplicates (by hex id) are marked, keeping the first occurrence valid.
 */
export function parseNoteIds(input: string): ParsedNoteInput[] {
  const tokens = input
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  const seen = new Set<string>();
  const results: ParsedNoteInput[] = [];

  for (const token of tokens) {
    const cleaned = token.replace(/^nostr:/i, '');
    let hex: string | undefined;
    let error: string | undefined;

    if (HEX64.test(cleaned)) {
      hex = cleaned.toLowerCase();
    } else if (/^note1/i.test(cleaned)) {
      try {
        const decoded = nip19.decode(cleaned.toLowerCase());
        if (decoded.type === 'note') hex = decoded.data;
        else error = 'Not a note ID';
      } catch {
        error = 'Could not read this note ID (typo?)';
      }
    } else if (/^nevent1/i.test(cleaned)) {
      try {
        const decoded = nip19.decode(cleaned.toLowerCase());
        if (decoded.type === 'nevent') hex = decoded.data.id;
        else error = 'Not an event ID';
      } catch {
        error = 'Could not read this event ID (typo?)';
      }
    } else if (/^(npub1|nprofile1)/i.test(cleaned)) {
      error = 'This is a profile, not a note';
    } else if (/^nsec1/i.test(cleaned)) {
      error = 'This looks like a private key — never share it!';
    } else if (/^naddr1/i.test(cleaned)) {
      error = 'Addressable events are not supported, use note1 or nevent1';
    } else {
      error = 'Not a recognized note ID format';
    }

    if (hex) {
      if (seen.has(hex)) {
        results.push({ raw: token, hex, status: 'duplicate' });
      } else {
        seen.add(hex);
        results.push({ raw: token, hex, status: 'valid' });
      }
    } else {
      results.push({ raw: token, status: 'invalid', error });
    }
  }

  return results;
}

/** Extract the unique valid hex ids from parsed input */
export function validNoteIds(parsed: ParsedNoteInput[]): string[] {
  return parsed.filter((p) => p.status === 'valid').map((p) => p.hex!);
}

// ---------------------------------------------------------------------------
// Trust scoring (transparent, rule-based)
// ---------------------------------------------------------------------------

export const TRUST_RULES = [
  { label: 'Has profile details', points: 15 },
  { label: 'Has a display name', points: 10 },
  { label: 'Has a profile picture', points: 10 },
  { label: 'Has a verified address (NIP-05)', points: 10 },
  { label: 'Follows other people', points: 10 },
  { label: 'Is followed by others', points: 15 },
  { label: 'Has activity beyond this raffle (3+ events)', points: 10 },
  { label: 'Very active (10+ events)', points: 5 },
  { label: 'Account is older than 30 days', points: 10 },
  { label: 'Followed by a trusted seed account', points: 15 },
  { label: 'Profile is marked as a bot', points: -30 },
] as const;

const DAY = 60 * 60 * 24;

/**
 * Compute a transparent 0–100 trust score from profile metadata and
 * activity signals. Every point added or missed produces a plain-language
 * reason so the raffle stays verifiable.
 */
export function scoreTrust(
  pubkey: string,
  metadata: NostrMetadata | undefined,
  activity: ActivitySignals | undefined,
  now: number = Math.floor(Date.now() / 1000),
): TrustResult {
  let score = 0;
  const positives: string[] = [];
  const negatives: string[] = [];

  const add = (cond: boolean, points: number, yes: string, no?: string) => {
    if (cond) {
      score += points;
      positives.push(`${yes} (+${points})`);
    } else if (no) {
      negatives.push(no);
    }
  };

  const hasProfile = Boolean(metadata && Object.keys(metadata).length > 0);
  add(hasProfile, 15, 'Has profile details', 'No profile details found');
  add(Boolean(metadata?.name || metadata?.display_name), 10, 'Has a display name', 'No display name');
  add(Boolean(metadata?.picture), 10, 'Has a profile picture', 'No profile picture');
  add(Boolean(metadata?.nip05), 10, 'Has a verified address (NIP-05)', 'No verified address');

  const act = activity;
  add(Boolean(act?.hasFollows), 10, `Follows ${act?.followCount ?? 0} people`, 'Follows nobody');
  add(Boolean(act?.hasFollowers), 15, 'Is followed by others', 'No followers found');

  const activityCount = act?.activityCount ?? 0;
  add(activityCount >= 3, 10, `Has ${activityCount} events beyond this raffle`, 'Little or no activity beyond this raffle');
  add(activityCount >= 10, 5, 'Very active account');

  const ageDays = act?.oldestEventAt ? Math.floor((now - act.oldestEventAt) / DAY) : 0;
  add(ageDays >= 30, 10, `Account is at least ${ageDays} days old`, 'Account looks very new');

  add(Boolean(act?.followedBySeed), 15, 'Followed by a trusted seed account');

  if (metadata?.bot === true) {
    score -= 30;
    negatives.push('Profile is marked as a bot (−30)');
  }

  score = Math.max(0, Math.min(100, score));

  return { pubkey, score, positives, negatives };
}

/** Build plain-language reasons why a participant was filtered out */
export function filterReasons(trust: TrustResult, minScore: number): string[] {
  const reasons: string[] = [];
  reasons.push(`Trust score ${trust.score} is below the minimum of ${minScore}`);
  // Surface the most meaningful missing signals
  for (const neg of trust.negatives) {
    reasons.push(neg);
  }
  return reasons;
}

// ---------------------------------------------------------------------------
// Tickets & winner logic
// ---------------------------------------------------------------------------

/** Generate a random hex seed for the ticket shuffle (crypto-grade randomness). */
export function generateShuffleSeed(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Assign ticket numbers 1..N using a seeded random shuffle.
 *
 * Each participant's position is determined by SHA-256(seed + ":" + pubkey),
 * sorted ascending. This makes the order:
 *
 * - RANDOM: nobody can predict their ticket number before the seed exists,
 *   so the order can't be gamed (unlike plain pubkey sorting).
 * - VERIFIABLE: given the published seed and the list of pubkeys, anyone
 *   can recompute the exact same ticket order with any SHA-256 tool.
 * - INDEPENDENT of the trust score: the score never enters the hash.
 */
export async function assignTickets(
  eligible: { participant: Participant; trust: TrustResult }[],
  seed: string,
): Promise<Ticket[]> {
  const hashed = await Promise.all(
    eligible.map(async (e) => ({
      entry: e,
      hash: await sha256Hex(`${seed}:${e.participant.pubkey}`),
    })),
  );
  hashed.sort((a, b) => a.hash.localeCompare(b.hash));
  return hashed.map((h, i) => ({
    number: i + 1,
    participant: h.entry.participant,
    trust: h.entry.trust,
  }));
}

/**
 * Pick winner and runner-up: the tickets closest to the drawn number.
 * Tie-break: the lower ticket number wins (ties are still reported).
 */
export function pickWinners(tickets: Ticket[], drawnNumber: number): DrawOutcome | null {
  if (tickets.length === 0) return null;

  const ranked = [...tickets].sort((a, b) => {
    const da = Math.abs(a.number - drawnNumber);
    const db = Math.abs(b.number - drawnNumber);
    if (da !== db) return da - db;
    return a.number - b.number; // tie-break: lower ticket wins
  });

  const winner = ranked[0];
  const winnerDistance = Math.abs(winner.number - drawnNumber);
  const winnerTies = ranked.filter(
    (t) => Math.abs(t.number - drawnNumber) === winnerDistance,
  );

  let runnerUp: Ticket | undefined;
  let runnerUpDistance: number | undefined;
  let runnerUpTies: Ticket[] = [];

  if (ranked.length > 1) {
    runnerUp = ranked[1];
    runnerUpDistance = Math.abs(runnerUp.number - drawnNumber);
    runnerUpTies = ranked.filter(
      (t) => t.number !== winner.number && Math.abs(t.number - drawnNumber) === runnerUpDistance,
    );
  }

  return {
    drawnNumber,
    winner,
    winnerDistance,
    winnerTies,
    runnerUp,
    runnerUpDistance,
    runnerUpTies,
  };
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** Only allow https URLs from untrusted event data (profile pictures etc.) */
export function sanitizeUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'https:') return parsed.href;
  } catch {
    // fallthrough
  }
  return undefined;
}

export function displayName(pubkey: string, metadata?: NostrMetadata): string {
  return (
    metadata?.display_name?.trim() ||
    metadata?.name?.trim() ||
    shortNpub(pubkey)
  );
}

export function shortNpub(pubkey: string): string {
  try {
    const npub = nip19.npubEncode(pubkey);
    return `${npub.slice(0, 12)}…${npub.slice(-4)}`;
  } catch {
    return pubkey.slice(0, 12) + '…';
  }
}

export function actionLabel(actions: EngagementAction[]): string {
  if (actions.length > 1) return 'Multiple';
  switch (actions[0]) {
    case 'like': return 'Like';
    case 'repost': return 'Repost';
    case 'comment': return 'Comment';
    default: return '—';
  }
}

export function formatTimestamp(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  });
}

// ---------------------------------------------------------------------------
// Exports (CSV, summary text, downloads)
// ---------------------------------------------------------------------------

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function toCsv(rows: string[][]): string {
  return rows.map((r) => r.map(csvEscape).join(',')).join('\r\n');
}

export function ticketsToCsv(
  tickets: Ticket[],
  profiles: Record<string, NostrMetadata>,
): string {
  const rows: string[][] = [
    ['ticket_number', 'npub', 'display_name', 'qualifying_actions', 'notes_engaged', 'trust_score'],
  ];
  for (const t of tickets) {
    rows.push([
      String(t.number),
      t.participant.npub,
      displayName(t.participant.pubkey, profiles[t.participant.pubkey]),
      t.participant.actions.join(' + '),
      String(t.participant.noteIds.length),
      String(t.trust.score),
    ]);
  }
  return toCsv(rows);
}

export function filteredOutToCsv(
  filtered: FilteredOutEntry[],
  profiles: Record<string, NostrMetadata>,
): string {
  const rows: string[][] = [
    ['npub', 'display_name', 'trust_score', 'reasons'],
  ];
  for (const f of filtered) {
    rows.push([
      f.participant.npub,
      displayName(f.participant.pubkey, profiles[f.participant.pubkey]),
      String(f.trust.score),
      f.reasons.join('; '),
    ]);
  }
  return toCsv(rows);
}

export interface SummaryData {
  notesScanned: number;
  eligibleTickets: number;
  drawnNumber: number;
  winnerName: string;
  winnerTicket: number;
  runnerUpName?: string;
  runnerUpTicket?: number;
  finalizedAt: number;
  drawAt: number;
}

export function buildSummaryText(d: SummaryData): string {
  const lines = [
    'Nostr Tombolo draw completed.',
    '',
    `Notes scanned: ${d.notesScanned}`,
    `Eligible tickets: ${d.eligibleTickets}`,
    `Drawn number: ${d.drawnNumber}`,
    '',
    `Winner: ${d.winnerName}`,
    `Winning ticket: ${d.winnerTicket}`,
  ];
  if (d.runnerUpName && d.runnerUpTicket !== undefined) {
    lines.push('', `Follow-up winner: ${d.runnerUpName}`, `Follow-up ticket: ${d.runnerUpTicket}`);
  }
  lines.push(
    '',
    `Ticket list finalized at: ${formatTimestamp(d.finalizedAt)}`,
    `Draw completed at: ${formatTimestamp(d.drawAt)}`,
  );
  return lines.join('\n');
}

export function buildFullReport(opts: {
  summary: SummaryData;
  noteIds: string[];
  minScore: number;
  settings: EngagementSettings;
  totals: EngagementTotals;
  tickets: Ticket[];
  filtered: FilteredOutEntry[];
  profiles: Record<string, NostrMetadata>;
  shuffleSeed: string;
}): string {
  const { summary, noteIds, minScore, settings, totals, tickets, filtered, profiles, shuffleSeed } = opts;
  const parts: string[] = [
    '=== NOSTR TOMBOLO — RAFFLE REPORT ===',
    '',
    buildSummaryText(summary),
    '',
    '--- Notes included ---',
    ...noteIds.map((id) => {
      try { return nip19.noteEncode(id); } catch { return id; }
    }),
    '',
    '--- Rules ---',
    `Likes count: ${settings.likes ? 'yes' : 'no'}`,
    `Reposts count: ${settings.reposts ? 'yes' : 'no'}`,
    `Comments count: ${settings.comments ? 'yes' : 'no'}`,
    'One ticket per unique person: yes (always)',
    `Minimum trust score: ${minScore}`,
    'Ticket order: random shuffle — position = SHA-256(seed + ":" + pubkey), sorted ascending',
    `Shuffle seed: ${shuffleSeed}`,
    'Winner rule: closest ticket to drawn number; ties go to the lower ticket',
    '',
    '--- Engagement found ---',
    `Likes: ${totals.likes}`,
    `Reposts: ${totals.reposts}`,
    `Comments: ${totals.comments}`,
    `Total events: ${totals.rawEvents}`,
    '',
    '--- Tickets ---',
    ...tickets.map((t) =>
      `#${t.number}  ${displayName(t.participant.pubkey, profiles[t.participant.pubkey])}  ${t.participant.npub}  (score ${t.trust.score}, ${t.participant.actions.join('+')})`,
    ),
    '',
    `--- Filtered out (${filtered.length}) ---`,
    ...filtered.map((f) =>
      `${displayName(f.participant.pubkey, profiles[f.participant.pubkey])}  ${f.participant.npub}  — ${f.reasons[0] ?? ''}`,
    ),
  ];
  return parts.join('\n');
}

export function downloadTextFile(filename: string, content: string, mime = 'text/plain'): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
